import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { formatApiError } from "../../lib/api";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Detect portal type for customized experience
  const portalType = searchParams.get('portal');
  const isEmployeePortal = portalType === 'employee';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const userData = await login(email, password);
      
      // Role-based routing to appropriate workspace
      let defaultPath = "/home";
      if (userData?.role === "employee" || userData?.role === "caregiver" || isEmployeePortal) {
        defaultPath = "/my-leynix";
      }
      
      const next = location.state?.from?.pathname || defaultPath;
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Customized content based on portal type
  const portalContent = isEmployeePortal ? {
    label: 'MY LEYNIX',
    title: 'Your personal\nworkspace awaits.',
    description: 'Clock in, view your schedule, access client information, and get the support you need — all in one place.',
    formTitle: 'Sign in to My Leynix',
    formSubtitle: 'Access your schedule, visits, and more.',
  } : {
    label: 'LEYNIX',
    title: 'Operations,\nin alignment.',
    description: 'The intelligent operations platform for service organizations. Workforce, scheduling, compliance, and payroll — all connected.',
    formTitle: 'Sign in to your account',
    formSubtitle: 'Welcome back. Please enter your credentials.',
  };

  return (
    <div className="min-h-screen flex">
      {/* Visual side — Leynix Brand Colors */}
      <div
        className="hidden lg:flex w-[42%] relative items-end p-12 text-white"
        style={{
          background: isEmployeePortal 
            ? "linear-gradient(165deg, #176DFF 0%, #1E88FF 50%, #0D1B33 100%)"
            : "linear-gradient(165deg, #0D1E3E 0%, #0D1B33 50%, #081428 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='%23176DFF' fill-opacity='0.3'/%3E%3C/svg%3E\")",
        }} />
        <div className="relative z-10 space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] opacity-70 text-white">{portalContent.label}</div>
          <h1 className="font-bold text-4xl xl:text-5xl leading-tight tracking-tight text-white whitespace-pre-line">
            {portalContent.title}
          </h1>
          <p className="text-white/70 text-base max-w-md">
            {portalContent.description}
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 lg:hidden">{portalContent.label}</div>
            <h2 className="text-2xl font-semibold tracking-tight">{portalContent.formTitle}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {portalContent.formSubtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-medium text-foreground block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-base"
                placeholder="you@agency.com"
                autoComplete="email"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-foreground">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-base"
                placeholder="••••••••"
                autoComplete="current-password"
                required
                data-testid="login-password"
              />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-xs text-muted-foreground text-center">
            By signing in you agree to the Leynix terms and acknowledge our privacy practices.
          </div>
        </div>
      </div>
    </div>
  );
}
